// Slice 9: Market Access Missions.
//
// Two layers:
//   1. Viren's contextual missions for THIS company (top of page) — generated
//      from Meera intel + readiness gaps + sector + stage. Lead with these
//      so the founder feels the system understands their company.
//   2. The 5 generic mission templates (kept as a fallback) below the
//      contextual list. Some founders will want the obvious options too.
import { useState } from "react";
import { Send, Briefcase, Users, Megaphone, Mic, ShieldQuestion } from "lucide-react";
import VirenSuggestionsPanel, { type MissionSuggestion } from "../../components/viren-suggestions-panel";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-founder-token": localStorage.getItem("founder_token") || "" });

const MISSIONS = [
  { key: "customer_interviews", title: "Customer interviews", icon: Users,
    detail: "10 structured calls with target buyers in 2 weeks. Output is a problem-fit memo." },
  { key: "enterprise_pilot", title: "Enterprise pilot push", icon: Briefcase,
    detail: "Get one signed paid pilot from an enterprise account in 4 weeks." },
  { key: "mentor_sessions", title: "Mentor sessions", icon: Mic,
    detail: "3 deep sessions with a senior operator in your sector over 4 weeks." },
  { key: "pr_push", title: "PR and visibility", icon: Megaphone,
    detail: "One byline, one podcast, one press mention in 6 weeks." },
  { key: "objection_practice", title: "Investor objection practice", icon: ShieldQuestion,
    detail: "Live drill on the 5 hardest investor questions. Recorded. With written feedback." },
];

async function postMissionToThread(body: string) {
  const r = await fetch(`${BASE}api/founder-portal/thread/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth() },
    body: JSON.stringify({ body }),
  });
  if (!r.ok) throw new Error("Could not send mission request to your Advisor.");
}

export default function FounderMissions() {
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());

  async function requestGeneric(m: { key: string; title: string }) {
    const ctx = window.prompt(`Tell your Advisor what you want from "${m.title}".`);
    if (!ctx || !ctx.trim()) return;
    setBusy(m.key);
    try {
      await postMissionToThread(`Mission request: ${m.title}\n\nWhat I want: ${ctx.trim()}`);
      setDone(s => new Set(s).add(m.key));
    } finally { setBusy(null); }
  }

  async function requestVirenMission(m: MissionSuggestion) {
    const body = `Mission request (suggested by Viren): ${m.title}\n\nWhat we will run: ${m.whatWeWillRun}\n\nSuccess looks like: ${m.successLooksLike}\n\nWhy now: ${m.whyNow}`;
    await postMissionToThread(body);
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Market Access Missions</div>
        <h1 className="text-2xl font-bold tracking-tight mb-2">Short, structured sprints that move the company</h1>
        <p className="text-sm text-white/65 leading-relaxed max-w-2xl">
          Viren has read your company and suggests a few missions tailored to where you are right now. The generic templates below are also available if you want to scope something different.
        </p>
      </div>

      <VirenSuggestionsPanel kind="mission" onRequestMission={requestVirenMission} />

      <div className="mb-3">
        <h2 className="text-sm font-semibold text-white/75">Generic mission templates</h2>
        <p className="text-[11px] text-white/60">Pick one if you want a standard sprint shape.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {MISSIONS.map(m => {
          const Icon = m.icon;
          const isDone = done.has(m.key);
          return (
            <div key={m.key} className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-[#FF5C00]" />
                <h3 className="text-sm font-bold">{m.title}</h3>
              </div>
              <p className="text-xs text-white/65 leading-relaxed mb-4">{m.detail}</p>
              {isDone ? (
                <div className="text-xs text-green-300">Sent to your Advisor.</div>
              ) : (
                <button
                  onClick={() => requestGeneric(m)} disabled={busy === m.key}
                  data-testid={`button-request-mission-${m.key}`}
                  className="inline-flex items-center gap-1.5 text-xs text-[#FF5C00] hover:text-[#FF5C00]/80 font-medium"
                ><Send className="w-3 h-3" /> {busy === m.key ? "Sending" : "Request this mission"}</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
